'use client';

import { forwardRef, useCallback, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { useControllableValue } from '../../hooks/use-controllable-value';
import { AlertIcon } from './alert-icon';
import { AlertRootContext } from './context/alert-root.context';
import { alertRecipe } from './styles/alert-root.styles';
import type { AlertRootPropsInterface } from './types/alert-root.types';

/**
 * @since 10.0.0
 */
export const AlertRoot = forwardRef<HTMLDivElement, AlertRootPropsInterface>(
  (props: AlertRootPropsInterface, forwardedRef) => {
    const {
      tone = 'info',
      children,
      className,
      css: cssProp,
      isOpen,
      onClose,
      role,
      ...rest
    } = props;

    const [openState, setOpenState] = useControllableValue<boolean | undefined>({
      controlledValue: isOpen,
      uncontrolledValue: true,
      isControlled: 'isOpen' in props,
      isUncontrolled: false,
      componentName: 'Alert.Root',
      controlledPropName: 'isOpen',
    });

    const handleClose = useCallback(() => {
      setOpenState(false);
      onClose?.();
    }, [onClose, setOpenState]);

    const contextValue = useMemo(
      () => ({
        tone,
        handleClose,
        isOpen: openState,
      }),
      [tone, handleClose, openState],
    );

    if (!openState) {
      return null;
    }

    const styles = css(alertRecipe.raw({ tone }), cssProp);
    const computedRole = role ?? (tone === 'critical' ? 'alert' : 'status');

    return (
      <AlertRootContext.Provider value={contextValue}>
        <div
          ref={forwardedRef}
          data-c11n-component="Alert.Root"
          data-c11n-version={__C11N_VERSION__}
          className={cx('c11n', 'c11n_alert__root', styles, className)}
          role={computedRole}
          {...rest}
        >
          <AlertIcon />
          {children}
        </div>
      </AlertRootContext.Provider>
    );
  },
);

AlertRoot.displayName = 'Alert.Root';
